import { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, FileText, CheckCircle2, AlertCircle, Building, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Import() {
  const [activeTab, setActiveTab] = useState('bank');
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [accountsRes, propsRes] = await Promise.all([
          axios.get(`${API}/bank-accounts`),
          axios.get(`${API}/corporations`)
        ]);
        setBankAccounts(accountsRes.data);
        setProperties(propsRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      let response;
      if (activeTab === 'bank') {
        response = await axios.post(`${API}/import/bank-statement?bank_account_id=${selectedAccount}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else if (activeTab === 'quickbooks') {
        response = await axios.post(`${API}/import/quickbooks${selectedProperty ? `?entity_id=${selectedProperty}` : ''}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else if (activeTab === 'units') {
        response = await axios.post(`${API}/import/units?entity_id=${selectedProperty}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else if (activeTab === 'leases') {
        response = await axios.post(`${API}/import/leases?entity_id=${selectedProperty}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      setResult({ success: true, message: response.data.message, data: response.data });
    } catch (error) {
      setResult({ success: false, message: error.response?.data?.detail || 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Import Data</h1>
        <p className="text-slate-500">Import bank statements and QuickBooks data</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'bank', label: 'Bank Statement', icon: FileText },
          { id: 'quickbooks', label: 'QuickBooks', icon: FileText },
          { id: 'units', label: 'Units', icon: Building },
          { id: 'leases', label: 'Leases', icon: Users }
        ].map((tab) => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); setResult(null); setFile(null); }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${activeTab === tab.id ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        {activeTab === 'bank' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900">Import Bank Statement</h3>
            <p className="text-sm text-slate-500">Upload a CSV file with columns: Date, Description, Amount</p>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Select Bank Account</label>
              <select value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg">
                <option value="">Select an account...</option>
                {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.entity_name} - {a.account_name}</option>)}
              </select>
            </div>

            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} className="hidden" id="bank-file" />
              <label htmlFor="bank-file" className="cursor-pointer">
                <span className="text-indigo-600 hover:text-indigo-700">Click to upload</span>
                <span className="text-slate-500"> or drag and drop</span>
              </label>
              <p className="text-xs text-slate-400 mt-2">CSV files only</p>
              {file && <p className="mt-2 text-sm text-slate-700">Selected: {file.name}</p>}
            </div>
          </div>
        )}

        {activeTab === 'quickbooks' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900">Import QuickBooks Data</h3>
            <p className="text-sm text-slate-500">Upload a QuickBooks export file (CSV format). Creates transactions and invoices.</p>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Property (optional)</label>
              <select value={selectedProperty} onChange={(e) => setSelectedProperty(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg">
                <option value="">All Properties</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>

            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
              <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <input type="file" accept=".csv,.qbo" onChange={(e) => setFile(e.target.files[0])} className="hidden" id="qb-file" />
              <label htmlFor="qb-file" className="cursor-pointer">
                <span className="text-indigo-600 hover:text-indigo-700">Click to upload</span>
                <span className="text-slate-500"> or drag and drop</span>
              </label>
              <p className="text-xs text-slate-400 mt-2">CSV or QBO files</p>
              {file && <p className="mt-2 text-sm text-slate-700">Selected: {file.name}</p>}
            </div>
          </div>
        )}

        {activeTab === 'units' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900">Import Units</h3>
            <p className="text-sm text-slate-500">Upload a CSV file with unit data. Required columns: Unit Number</p>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Select Property *</label>
              <select value={selectedProperty} onChange={(e) => setSelectedProperty(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg">
                <option value="">Select a property...</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>

            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
              <Building className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} className="hidden" id="units-file" />
              <label htmlFor="units-file" className="cursor-pointer">
                <span className="text-indigo-600 hover:text-indigo-700">Click to upload</span>
                <span className="text-slate-500"> or drag and drop</span>
              </label>
              <p className="text-xs text-slate-400 mt-2">CSV files only</p>
              {file && <p className="mt-2 text-sm text-slate-700">Selected: {file.name}</p>}
            </div>
          </div>
        )}

        {activeTab === 'leases' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900">Import Leases</h3>
            <p className="text-sm text-slate-500">Upload a CSV file with lease data. Required columns: Unit Number, Tenant Name</p>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Select Property *</label>
              <select value={selectedProperty} onChange={(e) => setSelectedProperty(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg">
                <option value="">Select a property...</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>

            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
              <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} className="hidden" id="leases-file" />
              <label htmlFor="leases-file" className="cursor-pointer">
                <span className="text-indigo-600 hover:text-indigo-700">Click to upload</span>
                <span className="text-slate-500"> or drag and drop</span>
              </label>
              <p className="text-xs text-slate-400 mt-2">CSV files only</p>
              {file && <p className="mt-2 text-sm text-slate-700">Selected: {file.name}</p>}
            </div>
          </div>
        )}

        <button onClick={handleImport} disabled={!file || importing || (activeTab === 'bank' && !selectedAccount) || ((activeTab === 'units' || activeTab === 'leases') && !selectedProperty)}
          className="mt-6 w-full py-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed">
          {importing ? 'Importing...' : 'Import Data'}
        </button>

        {result && (
          <div className={`mt-4 p-4 rounded-lg flex items-start gap-3 ${result.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'}`}>
            {result.success ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertCircle className="w-5 h-5 text-rose-600" />}
            <div>
              <p className={result.success ? 'text-emerald-700' : 'text-rose-700'}>{result.message}</p>
              {result.data && (
                <p className="text-sm text-slate-600 mt-1">Transactions: {result.data.count || result.data.transactions}</p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 className="font-medium text-amber-800 mb-2">Import Tips</h4>
        <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
          <li><strong>Bank Statement:</strong> CSV with columns: Date, Description, Amount</li>
          <li><strong>QuickBooks:</strong> Standard format with Type, Date, Name, Memo, Amount columns → Creates Transactions & Invoices</li>
          <li><strong>Units:</strong> CSV with: Unit Number, Type, Status, Bedrooms, Bathrooms, Square Feet, Floor, Monthly Rent</li>
          <li><strong>Leases:</strong> CSV with: Unit Number, Tenant Name, Tenant Email, Monthly Rent, Parking Fee, Start Date, End Date</li>
          <li>Dates should be in YYYY-MM-DD or MM/DD/YYYY format</li>
          <li>Positive amounts are income, negative are expenses</li>
        </ul>
      </div>
    </div>
  );
}
